/* global axios */
import ApiClient from './ApiClient';

class DealsAPI extends ApiClient {
  constructor() {
    super('deals', { accountScoped: true });
  }

  create(data) {
    const pipelineId = data.deal?.pipeline_id || data.pipeline_id;
    return axios.post(`${this.baseUrl()}/pipelines/${pipelineId}/deals`, data);
  }

  getAll(params = {}) {
    return axios.get(`${this.baseUrl()}/deals`, { params });
  }

  getDashboardStats() {
    return axios.get(`${this.baseUrl()}/deals/dashboard_stats`);
  }

  show(id) {
    return axios.get(`${this.baseUrl()}/deals/${id}`);
  }

  getThumbnails(displayIds) {
    return axios.post(`${this.baseUrl()}/deals/thumbnails`, {
      display_ids: displayIds,
    });
  }

  getByPipeline(pipelineId, params = {}) {
    return axios.get(`${this.baseUrl()}/pipelines/${pipelineId}/deals`, {
      params,
    });
  }

  moveStage(pipelineId, dealId, data) {
    return axios.post(
      `${this.baseUrl()}/pipelines/${pipelineId}/deals/${dealId}/move_stage`,
      data
    );
  }

  move(pipelineId, dealId, data) {
    return axios.post(
      `${this.baseUrl()}/pipelines/${pipelineId}/deals/${dealId}/move`,
      data
    );
  }

  changeStatus(pipelineId, dealId, data) {
    return axios.post(
      `${this.baseUrl()}/pipelines/${pipelineId}/deals/${dealId}/change_status`,
      data
    );
  }

  toggleTimer(pipelineId, dealId) {
    return axios.post(
      `${this.baseUrl()}/pipelines/${pipelineId}/deals/${dealId}/toggle_timer`
    );
  }

  updateChecklist(pipelineId, dealId, checklist) {
    return axios.patch(
      `${this.baseUrl()}/pipelines/${pipelineId}/deals/${dealId}/update_checklist`,
      { checklist }
    );
  }

  createChecklistItem(pipelineId, dealId, data) {
    return axios.post(
      `${this.baseUrl()}/pipelines/${pipelineId}/deals/${dealId}/create_checklist_item`,
      data
    );
  }

  toggleChecklistItem(pipelineId, dealId, checklistItemId) {
    return axios.post(
      `${this.baseUrl()}/pipelines/${pipelineId}/deals/${dealId}/toggle_checklist_item`,
      { checklist_item_id: checklistItemId }
    );
  }

  assignAgentToChecklistItem(pipelineId, dealId, checklistItemId, agentId) {
    return axios.post(
      `${this.baseUrl()}/pipelines/${pipelineId}/deals/${dealId}/assign_agent_to_checklist_item`,
      { checklist_item_id: checklistItemId, agent_id: agentId }
    );
  }

  removeAgentFromChecklistItem(pipelineId, dealId, checklistItemId) {
    return axios.delete(
      `${this.baseUrl()}/pipelines/${pipelineId}/deals/${dealId}/remove_agent_from_checklist_item`,
      { data: { checklist_item_id: checklistItemId } }
    );
  }

  duplicateChecklist(pipelineId, dealId, targetItemId) {
    return axios.post(
      `${this.baseUrl()}/pipelines/${pipelineId}/deals/${dealId}/duplicate_checklist`,
      { target_item_id: targetItemId }
    );
  }

  createNote(pipelineId, dealId, data) {
    return axios.post(
      `${this.baseUrl()}/pipelines/${pipelineId}/deals/${dealId}/create_note`,
      data
    );
  }

  assignAgent(pipelineId, dealId, agentId) {
    return axios.post(
      `${this.baseUrl()}/pipelines/${pipelineId}/deals/${dealId}/assign_agent`,
      { agent_id: agentId }
    );
  }

  removeAgent(pipelineId, dealId, agentId) {
    return axios.delete(
      `${this.baseUrl()}/pipelines/${pipelineId}/deals/${dealId}/remove_agent`,
      { data: { agent_id: agentId } }
    );
  }

  getAssignedAgents(pipelineId, dealId) {
    return axios.get(
      `${this.baseUrl()}/pipelines/${pipelineId}/deals/${dealId}/assigned_agents`
    );
  }

  getTimeReport(pipelineId, dealId) {
    return axios.get(
      `${this.baseUrl()}/pipelines/${pipelineId}/deals/${dealId}/time_report`
    );
  }

  getStageTimeBreakdown(pipelineId, dealId) {
    return axios.get(
      `${this.baseUrl()}/pipelines/${pipelineId}/deals/${dealId}/stage_time_breakdown`
    );
  }

  reorder(pipelineId, items) {
    return axios.post(
      `${this.baseUrl()}/pipelines/${pipelineId}/deals/reorder`,
      { items }
    );
  }

  bulkMoveItems(pipelineId, data) {
    return axios.post(
      `${this.baseUrl()}/pipelines/${pipelineId}/deals/bulk_move_items`,
      data
    );
  }

  bulkAssignAgent(pipelineId, data) {
    return axios.post(
      `${this.baseUrl()}/pipelines/${pipelineId}/deals/bulk_assign_agent`,
      data
    );
  }

  bulkSetPriority(pipelineId, data) {
    return axios.post(
      `${this.baseUrl()}/pipelines/${pipelineId}/deals/bulk_set_priority`,
      data
    );
  }

  debug(pipelineId) {
    return axios.get(`${this.baseUrl()}/pipelines/${pipelineId}/deals/debug`);
  }

  getAttributeValues(pipelineId) {
    return axios.get(`${this.baseUrl()}/deals/attribute_values`, {
      params: { pipeline_id: pipelineId },
    });
  }

  addContact(pipelineId, dealId, contactId) {
    return axios.post(
      `${this.baseUrl()}/pipelines/${pipelineId}/deals/${dealId}/add_contact`,
      { contact_id: contactId }
    );
  }

  removeContact(pipelineId, dealId, contactId) {
    return axios.delete(
      `${this.baseUrl()}/pipelines/${pipelineId}/deals/${dealId}/remove_contact`,
      { data: { contact_id: contactId } }
    );
  }

  syncConversations(pipelineId, dealId, contactId, conversationIds) {
    return axios.post(
      `${this.baseUrl()}/pipelines/${pipelineId}/deals/${dealId}/sync_conversations`,
      { contact_id: contactId, conversation_ids: conversationIds }
    );
  }

  getTimeline(pipelineId, dealId, params = {}) {
    return axios.get(
      `${this.baseUrl()}/pipelines/${pipelineId}/deals/${dealId}/timeline`,
      { params }
    );
  }

  destroy(pipelineId, dealId) {
    return axios.delete(
      `${this.baseUrl()}/pipelines/${pipelineId}/deals/${dealId}`
    );
  }

  export(params = {}) {
    return axios.post(`${this.baseUrl()}/deals/export`, params, {
      responseType: 'blob',
    });
  }
}

export default new DealsAPI();
